import React, { useContext, useState } from 'react'
import {
  ExchangeColor,
  ProductSizes,
  ReturnReason,
  ReturnsProduct
} from '@api/returns/types'
import {
  ExchangeOptions,
  ExchangesSizeOption,
  ReasonListItemField
} from '@components/returns/types'
import { ValuesState, PartialState } from '../index'
import ReturnsContext from '../../context'
import useExchangeSteps from './useExchangeSteps'
import { SHORTSLEG } from '@consts/index'
import { objectKeys } from '@helpers/utils'

type ExchangeFormProps = {
  selectedProduct: ReturnsProduct
  onDone: () => void
  setValues: (partialState: PartialState<ValuesState>) => void
  values: ValuesState
  reasons: ReturnReason[]
  stepIndex: number
}

function getExchangesValues(productExchangeOptions: ExchangeOptions = []) {
  return productExchangeOptions.map(option => ({
    id: option.type.toLowerCase(),
    question: new RegExp(`${SHORTSLEG}`).test(option.type)
      ? 'Leg'
      : option.name,
    required: false,
    answers: option.values
  })) as ExchangesSizeOption[]
}

function reasonFilter(exchangeColors: ExchangeColor[]) {
  return (reason: ReturnReason) => {
    // if (!exchangeColors?.length  && reason.id === RETURN_REASON_CODES.COLOR) {
    //   return false
    // } // @TODO TO BE FIXED AS PART OF RETURN OBJECT

    return !/^51|58|2$/.test(String(reason.id))
  }
}

function isEqualFitData(
  selectedProductSizes: Partial<ProductSizes>,
  savedFitSizes: PartialState<ValuesState['sizes']>
) {
  return objectKeys(savedFitSizes).every(key => {
    const updatedSizesValue = savedFitSizes[key as keyof ProductSizes]
    const selectedProductValue = selectedProductSizes[key as keyof ProductSizes]
    return String(updatedSizesValue) === String(selectedProductValue)
  })
}

function ExchangeForm({
  selectedProduct,
  values,
  stepIndex = 0,
  setValues,
  onDone,
  reasons
}: ExchangeFormProps) {
  const { storedSavedFit, setStoredSavedFit } = useContext(ReturnsContext)
  const {
    exchange_options: exchangeOptions,
    exchange_colors: exchangeColors,
    sizes
  } = selectedProduct

  const exchangeValues = getExchangesValues(exchangeOptions)
  const exchangesSizeOptions = exchangeValues.filter(obj =>
    objectKeys(sizes).find(key => key === obj.id)
  )

  const hasStoredSavedFit =
    !storedSavedFit || objectKeys(storedSavedFit || {}).length === 0

  const reasonList = reasons
    .filter(reasonFilter(exchangeColors))
    .map(reason => ({
      label: reason.title,
      value: reason.id,
      fields: reason.fields,
      available: true
    }))

  const additionalReasonFields: ReasonListItemField[] | undefined =
    reasonList.find(reason => reason.value === values.reason)?.fields

  const [currentStep, setCurrentStep] = useState(stepIndex)

  const showAdditionalFields =
    !!additionalReasonFields?.length &&
    hasStoredSavedFit &&
    isEqualFitData(sizes, storedSavedFit)

  const steps = useExchangeSteps({
    reason: values.reason,
    colors: exchangeColors,
    showAdditionalFields
  })

  const CurrentStepComponent = steps[currentStep]
  const isLastStep = currentStep === steps.length - 1

  const setSelection = (obj: PartialState<ValuesState>) => {
    setValues(obj)
  }

  const handleUpdate = (step = 'next') => {
    if (step === 'done') {
      onDone()
    } else {
      if (step === 'prev' && currentStep <= 1) {
        setStoredSavedFit({})
      }
      setCurrentStep(currentActiveStepIndex =>
        step === 'next'
          ? currentActiveStepIndex + 1
          : currentActiveStepIndex - 1
      )
    }
  }

  return (
    <CurrentStepComponent
      selectedProduct={selectedProduct}
      reasonList={reasonList}
      setSelection={setSelection}
      sizes={sizes}
      values={values}
      exchangesSizeOptions={exchangesSizeOptions}
      onUpdate={handleUpdate}
      isLastStep={isLastStep}
      exchangeColors={exchangeColors}
    />
  )
}

export default ExchangeForm
